import React, {useState} from 'react';
import OdxModal from "../../../../OdxComponents/NativeOdx/Modal/Odx-Modal";
import OdxInputField from "../../../../OdxComponents/NativeOdx/FormControls/Odx-InputField";
import {odxIconSets} from "../../../../OdxComponents/constants";
import {HubPathRouting} from "../../../../HubFramework/pathBuilder";
import {createApiKeys} from "../../BackendFacade/apiCalls";
import {useMain} from "../../../../Utils/SessionControls/mainContext";
import {TenantLine} from "../../../IDMS/Interfaces/ITenantSystem";
import {defaultTenantSystem} from "../../constants";
import {IApiKeyContentTypes} from "../../Interfaces/IApiKeyManagement";
import {deepCopy, formatDateToBackend} from "../../../../Utils/transformer";
import {ScopeType} from "../../../IDMS/Utils/tenantSystem";
import OdxCheckbox from "../../../../OdxComponents/NativeOdx/FormControls/Odx-Checkbox";
import AddEditScopeModal from "../../../IDMS/Tabs/Modals/AddEditScopeModal";
import ModalTenantManagementTable from "../../../../OdxComponents/CustomOdx/ModalTenantManagementTable";
import OdxFormfield from "../../../../OdxComponents/NativeOdx/FormControls/Odx-Formfield";
import OdxDatepicker from "../../../../OdxComponents/NativeOdx/FormControls/Odx-Datepicker";

interface AddApiKeyModalProps {
    toggleOpen: () => void;
    closeFunction: () => void;
    onSuccessFunction: () => void;
    projectId: string;
}

const ApiKeysAddModal: React.FC<AddApiKeyModalProps> = ({
                                                            toggleOpen,
                                                            closeFunction,
                                                            onSuccessFunction,
                                                            projectId,
                                                        }) => {
    const baseId = 'hub-add-api-key';

    const defaultValue = {
        displayName: "",
        secondaryApiKey: "",
        primaryApiKey: "",
        projectId: projectId,
        apiKeyId: "",
        content: "",
        contentType: IApiKeyContentTypes.hubIdmsTenantJson,
        lastUsedOn: "",
        validUntil: "",
    };

    const [tenantSystem, setTenantSystem] = useState<TenantLine[]>(defaultTenantSystem);
    const [Selected_Value, setSelected_Value] = useState(defaultValue);
    const [validUntil, setValidUntil] = useState("");
    const [editingScope, setEditingScope] = useState<TenantLine | null>(null);
    const [isAddEditScopeSideSheetOpen, setIsAddEditScopeSideSheetOpen] = useState(false);

    const {setLoading, createToast, createErrorToast} = useMain();

    const saveKey = async () => {
        if (Selected_Value.contentType === IApiKeyContentTypes.hubIdmsTenantJson && tenantSystem.length === 0) {
            createErrorToast("Please add at least one scope");
            return;
        }

        let tenantSystemToSave: TenantLine[] = deepCopy(tenantSystem);

        // remove global level when on project level from tenant system, not allowed in backend
        if (HubPathRouting.currentProject) {
            tenantSystemToSave = tenantSystemToSave.filter(scope => scope.fullscope !== "user");
        }

        setLoading(true);
        if (Selected_Value.contentType === IApiKeyContentTypes.hubIdmsTenantJson) {
            let tenantData: { [key: string]: string } = {};
            for (let line of tenantSystemToSave) {
                if (line.value === "-" || line.scopes.length === 0 || line.value === "") {
                    continue;
                }
                tenantData[line.scopes.join('/')] = line.value;
            }
            Selected_Value.content = JSON.stringify(tenantData);
        }

        if (validUntil !== "") {
            Selected_Value.validUntil = formatDateToBackend(validUntil);
        }

        createApiKeys(projectId, Selected_Value).then((data) => {
            setLoading(false);
            if (data.success) {
                onSuccessFunction();
                createToast({message: "API Key saved successfully", variant: "success", title: "Success"});
            } else {
                createErrorToast("Error while saving API key: " + data.message)
            }
        });
    };

    const removeScope = (index: number) => {
        setTenantSystem(tenantSystem.filter((scope, i) => i !== index));
    };

    const openEditScopeModal = (scope: TenantLine) => {
        setEditingScope(scope);
        setIsAddEditScopeSideSheetOpen(true);
    };

    const handleAddEditScope = (newScope: TenantLine) => {
        if (editingScope) {
            const updatedScopes = tenantSystem.map(scope => scope.id === editingScope.id ? newScope : scope);
            setTenantSystem(updatedScopes);
        } else {
            // update the id to be unique
            newScope.id = tenantSystem.length + 1;
            setTenantSystem([...tenantSystem, newScope]);
        }
        setEditingScope(null);
        setIsAddEditScopeSideSheetOpen(false);
    };


    return (<>
        {isAddEditScopeSideSheetOpen && <AddEditScopeModal
            onClose={() => {
                setIsAddEditScopeSideSheetOpen(false);
                setEditingScope(null);
            }}
            onAddScope={handleAddEditScope}
            initialScope={editingScope}
            isEditMode={!!editingScope}
            scopeType={ScopeType.Hub}
            globalScope={editingScope && editingScope.fullscope === "user"}
        />}
        <OdxModal
            size="small"
            title={"Add Api Key"}
            onClose={toggleOpen}
            id={baseId}
            isOpen={!isAddEditScopeSideSheetOpen}
            variant="sidesheet"
            footerButtons={[
                <odx-button
                    key="close"
                    id={`OdxButton_${baseId}-close-btn`}
                    variant="ghost"
                    size="lg"
                    type="button"
                    onClick={toggleOpen}
                >
                    Close
                </odx-button>,
                <odx-button
                    key="submit"
                    id={`OdxButton_${baseId}-submit-btn`}
                    variant="primary"
                    size="lg"
                    type="button"
                    onClick={() => saveKey()}
                >
                    Submit
                </odx-button>
            ]}
        >
            <div id={`${baseId}-content`}>
                <OdxInputField
                    isRequired={true}
                    id={`${baseId}-name`}
                    label={'Name'}
                    value={Selected_Value.displayName}
                    onChange={(event) => {
                        const newValue = event.target.value;
                        setSelected_Value(prevState => ({
                            ...prevState,
                            displayName: newValue
                        }));
                    }}
                />

                <OdxDatepicker isRequired={false} index={`${baseId}-expiration`} label={"Expiration Date"} value={validUntil}
                               setValue={setValidUntil}/>
                <odx-separator id={`${baseId}-separator-top`} style={{margin: '1rem 0'}} />
                <OdxCheckbox label={"TenantSystem"}
                             id={`${baseId}-tenant-toggle`}
                             checked={Selected_Value.contentType === IApiKeyContentTypes.hubIdmsTenantJson}
                             onChange={(event) => {
                                 setSelected_Value(prevState => ({
                                     ...prevState,
                                     contentType: event.target.checked ? IApiKeyContentTypes.hubIdmsTenantJson : IApiKeyContentTypes.custom
                                 }));
                             }}
                />
                <odx-separator id={`${baseId}-separator-bottom`} style={{margin: '1rem 0'}} />

                {Selected_Value.contentType === IApiKeyContentTypes.hubIdmsTenantJson && (
                    <>
                        <ModalTenantManagementTable scopes={tenantSystem} openEditScopeModal={openEditScopeModal}
                                                    removeScope={removeScope}/>
                        {!editingScope && (
                            <odx-button
                                variant="ghost"
                                size="sm"
                                type="button"
                                onClick={() => setIsAddEditScopeSideSheetOpen(true)}
                                id={`OdxButton_${baseId}-add-scope-btn`}
                            >
                                <odx-icon slot="prefix" name={`${odxIconSets.core}::plus`} size="sm"></odx-icon>
                                Add Scope
                            </odx-button>
                        )}
                    </>
                )}
                {Selected_Value.contentType !== IApiKeyContentTypes.hubIdmsTenantJson && (
                    <>
                        <OdxFormfield isRequired={true}
                                      index={`${baseId}-custom-scope`}
                                      label={"Role"}
                                      value={Selected_Value.content}
                                      onChange={(event) => {
                                          const newValue = event.target.value;
                                          setSelected_Value(prevState => ({
                                              ...prevState,
                                              content: newValue
                                          }));
                                      }}/>
                    </>
                )}
            </div>
        </OdxModal>
    </>);
};

export default ApiKeysAddModal;
